import { workItemBodyFields, isWorkItemValue } from './work-item-selection';
/**
 * The JSA statuses the API's POST /safety/jsa/:id/submit accepts (api#811).
 *
 * A rejected JSA is resubmitted rather than rewritten, so both office surfaces
 * that offer Submit — the records row and the detail modal — read this one
 * list. Before api#811 only a draft could be submitted and a rejection was a
 * dead end on every screen.
 */
export const JSA_SUBMITTABLE_STATUSES: readonly string[] = ['draft', 'rejected'];

export function isJsaSubmittable(status: string | null | undefined): boolean {
  return !!status && JSA_SUBMITTABLE_STATUSES.includes(status);
}

/**
 * What a rejected JSA says about why. A rejection with no reason on the row is
 * a gap in the record, not "no problem" — every rejection made from the office
 * screen before api#811 stored none — so it says so rather than rendering
 * nothing.
 */
export function rejectionReasonText(reason: string | null | undefined): string {
  const r = (reason ?? '').trim();
  return r ? r : 'No reason was recorded with the rejection.';
}

/**
 * JSAForm's state → the POST /safety/jsa body. The form keeps the chosen
 * supervisor as an id (sent) and a name (the signature's label only).
 */
export function transformJsaData(data: any) {
  // A PT job is a work item, never a jobId (api#976): jobId stays null and the
  // pair is appended below. A casing or general pick adds no keys at all.
  const workItem = workItemBodyFields(data.jobId);
  return {
    jobId: data.jobId === '__general__' || !data.jobId || isWorkItemValue(data.jobId) ? null : data.jobId,
    ...workItem,
    title: data.template || 'Job Safety Analysis',
    // The chosen supervisor's user id, or omitted when nobody was picked — the
    // API then stores NULL rather than defaulting to the author (api#811).
    supervisorId: data.reviewedById || undefined,
    jobSteps: (data.steps || []).map((s: any) => ({
      title: s.title,
      hazards: s.hazards,
      controls: s.controls,
      ppe: s.ppe,
    })),
    // Omitted when empty: POST stores NULL / [] for an absent key, which is
    // what an empty box means.
    emergencyProcedures: jsaText(data.emergencyProcedures) ?? undefined,
    weatherConsiderations: jsaText(data.weatherConsiderations) ?? undefined,
    siteSpecificHazards: normalizeJsaList(data.siteSpecificHazards),
    crewSignatures: [
      ...Object.entries(data.crewSignatures || {}).map(([name, sig]) => ({
        name,
        signatureR2Key: sig || undefined,
        signedAt: new Date().toISOString(),
        role: 'crew' as const,
      })),
      ...(data.supervisorSignatureKey
        ? [{
            name: data.reviewedBy || 'Supervisor',
            signatureR2Key: data.supervisorSignatureKey,
            signedAt: new Date().toISOString(),
            role: 'supervisor' as const,
          }]
        : []),
    ],
  };
}

/**
 * A JSA job step, read in either of the two shapes that reach job_steps.
 *
 *   JSAForm (transformJsaData above)   {title, hazards, controls, ppe}
 *   the agent's create_jsa             {task, hazards, controls}
 *
 * The detail modal read `title || description`, so every agent-written step
 * rendered as a fabricated "Step N". A step with no name in either shape
 * renders as an em dash, not an invented label.
 *
 * The agent's older steps carry no PPE: before api#813 its PPE was written to
 * the JSA's site_specific_hazards column, so on those JSAs it shows (and now
 * edits) under Site-Specific Hazards, indistinguishable from a real hazard.
 */
export interface JsaStepView {
  label: string;
  hazards: string[];
  controls: string[];
  ppe: string[];
}

const stringList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : [];

const firstText = (...vals: unknown[]): string | null => {
  for (const v of vals) if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
};

export function readJsaStep(step: any): JsaStepView {
  return {
    label: firstText(step?.title, step?.task, step?.description) ?? '—',
    hazards: stringList(step?.hazards),
    controls: stringList(step?.controls),
    ppe: stringList(step?.ppe),
  };
}

/**
 * The rejected JSAs a field user wrote — the ones only they can be expected to
 * fix. GET /safety/jsa returns the whole tenant's list, camelCase, with
 * `createdBy`. With no user id nothing is "theirs", so the answer is empty
 * rather than everyone's rejections.
 */
export function rejectedJsasAuthoredBy(jsas: any[], userId: string | null | undefined): any[] {
  if (!userId) return [];
  return jsas.filter(j => j?.status === 'rejected' && (j.createdBy ?? j.created_by) === userId);
}

/* ────────────── editing an existing JSA ────────────── */

/**
 * The states PUT /safety/jsa/:id accepts (the API's JSA_EDITABLE_STATUSES).
 * The same two as submit, but a separate name: they answer different questions
 * and the API keeps them as separate lists.
 */
export const JSA_EDITABLE_STATUSES: readonly string[] = ['draft', 'rejected'];

export function isJsaEditable(status: string | null | undefined): boolean {
  return !!status && JSA_EDITABLE_STATUSES.includes(status);
}

/**
 * Where the edit screen lives for the reader's route tree. The detail modal
 * is mounted on the office records screen, /field/safety and /driver/safety,
 * and each tree carries its own copy of the edit route.
 */
export function jsaEditPath(id: string, pathname: string): string {
  if (pathname.startsWith('/field')) return `/field/safety/jsa/${id}/edit`;
  if (pathname.startsWith('/driver')) return `/driver/safety/jsa/${id}/edit`;
  return `/safety/jsa/${id}/edit`;
}

/** A step as the form edits it, and as PUT writes it: the form's shape. */
export interface JsaEditStep {
  title: string;
  hazards: string[];
  controls: string[];
  ppe: string[];
}

/**
 * A stored step, in either shape (see readJsaStep), as an editable step. Unlike
 * readJsaStep this never invents a label: a step with no name edits as an
 * empty title, not as "—".
 */
export function readJsaStepForEdit(step: any): JsaEditStep {
  const view = readJsaStep(step);
  return {
    title: firstText(step?.title, step?.task, step?.description) ?? '',
    hazards: view.hazards,
    controls: view.controls,
    ppe: view.ppe,
  };
}

/** A list of strings, trimmed, blanks and non-strings dropped. */
export function normalizeJsaList(v: unknown): string[] {
  return Array.isArray(v) ? v.map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean) : [];
}

/** A free-text field, trimmed; empty is null — the column's "nothing written". */
export function jsaText(v: unknown): string | null {
  const t = typeof v === 'string' ? v.trim() : '';
  return t ? t : null;
}

/**
 * Trimmed, blank entries dropped. Applied to both sides before comparing, so an
 * empty hazard row someone added and left is not an edit, and to the body before
 * it is sent, so the blank row is not stored.
 */
export function normalizeJsaSteps(steps: any[]): JsaEditStep[] {
  const clean = normalizeJsaList;
  return (steps || []).map((s) => ({
    title: typeof s?.title === 'string' ? s.title.trim() : '',
    hazards: clean(s?.hazards),
    controls: clean(s?.controls),
    ppe: clean(s?.ppe),
  }));
}

/** Key-order-independent JSON — the same comparison the API's audit diff uses. */
export function stableJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v as object).sort().map((k) => `${JSON.stringify(k)}:${stableJson((v as any)[k])}`).join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

/** The signature a stored entry carries, in any of the spellings that have been written. */
export function jsaSignatureKey(entry: any): string | null {
  const k = entry?.signatureR2Key ?? entry?.signature_r2_key ?? entry?.signature ?? entry?.signature_url ?? entry?.signatureUrl;
  return typeof k === 'string' && k ? k : null;
}

const isSupervisorEntry = (entry: any) => entry?.role === 'supervisor';

/**
 * GET /safety/jsa/:id (a raw `jsa.*`, so snake_case; camelCase tolerated) →
 * the parts of JSAForm's state an edit starts from. Signatures come back as the
 * form holds them: crew as a name → key map, the supervisor's as one key.
 */
export function jsaToFormState(jsa: any) {
  const sigs: any[] = jsa?.crew_signatures ?? jsa?.crewSignatures ?? [];
  const crewSignatures: Record<string, string> = {};
  let supervisorSignatureKey = '';
  for (const s of Array.isArray(sigs) ? sigs : []) {
    const key = jsaSignatureKey(s);
    if (!key) continue;
    if (isSupervisorEntry(s)) supervisorSignatureKey = key;
    else if (typeof s?.name === 'string' && s.name) crewSignatures[s.name] = key;
  }
  const steps: any[] = jsa?.job_steps ?? jsa?.jobSteps ?? [];
  const text = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    title: (jsa?.title ?? '') as string,
    reviewedById: (jsa?.supervisor_id ?? jsa?.supervisorId ?? '') as string,
    reviewedBy: (jsa?.supervisor_name ?? jsa?.supervisorName ?? '') as string,
    supervisorSignatureKey,
    crewSignatures,
    steps: (Array.isArray(steps) ? steps : []).map(readJsaStepForEdit),
    emergencyProcedures: text(jsa?.emergency_procedures ?? jsa?.emergencyProcedures),
    weatherConsiderations: text(jsa?.weather_considerations ?? jsa?.weatherConsiderations),
    siteSpecificHazards: stringList(jsa?.site_specific_hazards ?? jsa?.siteSpecificHazards),
  };
}

export interface JsaEditFormState {
  title: string;
  reviewedById: string;
  reviewedBy: string;
  supervisorSignatureKey: string;
  crewSignatures: Record<string, string>;
  steps: any[];
  emergencyProcedures?: string;
  weatherConsiderations?: string;
  siteSpecificHazards?: string[];
}

/**
 * What the crew signs, beyond the steps — the API's JSA_SIGNED_CONTENT
 * (api#815) is these three plus jobSteps. A change to any one clears every
 * stored signature there, so it must here too, or the warning undercounts
 * what the save is about to do.
 */
export type JsaSignedPart = 'steps' | 'siteSpecificHazards' | 'emergencyProcedures' | 'weatherConsiderations';

export const JSA_SIGNED_PART_LABELS: Record<JsaSignedPart, string> = {
  steps: 'the steps',
  siteSpecificHazards: 'the site-specific hazards',
  emergencyProcedures: 'the emergency procedures',
  weatherConsiderations: 'the weather considerations',
};

/**
 * THE SIGNATURE RULE FOR AN EDIT. A crew signature says "I reviewed THESE
 * hazards and controls", so it cannot outlive a change to them:
 *
 *   - the signed content changed   → every signature already on the JSA is dropped
 *     (the steps, site-specific hazards, emergency procedures or weather
 *     considerations — see JsaSignedPart)
 *   - the named supervisor changed → the old supervisor's signature is dropped
 *   - otherwise                    → stored signatures are kept VERBATIM
 *
 * A signature is told apart by its KEY, not its name. A key captured in this
 * edit is new and always kept; a key that was already stored is old. So a
 * crew member who re-signs after the change is kept, and an old signature
 * echoed back is not — the same comparison the API rule will make.
 *
 * Kept entries are sent as stored, never rebuilt: transformJsaData stamps
 * `signedAt` with the current time, and running a kept signature through it
 * would re-date it to the edit. When nothing about the signatures changed,
 * `crewSignatures` is left out of the body entirely.
 *
 * "Changed" is judged on normalized content against normalized content, so an
 * agent-written step ({task}) read into the form's shape ({title}) and saved
 * untouched is not a change, and does not clear anyone.
 */
export function buildJsaUpdate(jsa: any, form: JsaEditFormState) {
  const baseline = jsaToFormState(jsa);
  const stepsBefore = normalizeJsaSteps(baseline.steps);
  const stepsAfter = normalizeJsaSteps(form.steps);
  const stepsChanged = stableJson(stepsBefore) !== stableJson(stepsAfter);

  // Normalized on both sides, like the steps: a trailing space or an empty
  // hazard row left behind is not an edit, and does not clear anyone.
  const hazardsBefore = normalizeJsaList(baseline.siteSpecificHazards);
  const hazardsAfter = normalizeJsaList(form.siteSpecificHazards ?? baseline.siteSpecificHazards);
  const emergencyBefore = jsaText(baseline.emergencyProcedures);
  const emergencyAfter = jsaText(form.emergencyProcedures ?? baseline.emergencyProcedures);
  const weatherBefore = jsaText(baseline.weatherConsiderations);
  const weatherAfter = jsaText(form.weatherConsiderations ?? baseline.weatherConsiderations);

  const changedParts: JsaSignedPart[] = [];
  if (stepsChanged) changedParts.push('steps');
  if (stableJson(hazardsBefore) !== stableJson(hazardsAfter)) changedParts.push('siteSpecificHazards');
  if (emergencyBefore !== emergencyAfter) changedParts.push('emergencyProcedures');
  if (weatherBefore !== weatherAfter) changedParts.push('weatherConsiderations');
  const contentChanged = changedParts.length > 0;

  const supervisorBefore = baseline.reviewedById || null;
  const supervisorAfter = form.reviewedById || null;
  const supervisorChanged = supervisorBefore !== supervisorAfter;

  const stored: any[] = Array.isArray(jsa?.crew_signatures ?? jsa?.crewSignatures)
    ? (jsa.crew_signatures ?? jsa.crewSignatures)
    : [];
  const storedKeys = new Set(stored.map(jsaSignatureKey).filter(Boolean) as string[]);

  const signatures: any[] = [];
  let cleared = 0;
  for (const entry of stored) {
    const key = jsaSignatureKey(entry);
    if (!key) { signatures.push(entry); continue; } // nobody signed it; nothing to clear
    const supervisor = isSupervisorEntry(entry);
    if (contentChanged || (supervisor && supervisorChanged)) { cleared += 1; continue; }
    const stillHeld = supervisor
      ? form.supervisorSignatureKey === key
      : form.crewSignatures?.[entry.name] === key;
    if (stillHeld) signatures.push(entry);
  }
  const now = new Date().toISOString();
  for (const [name, key] of Object.entries(form.crewSignatures || {})) {
    if (key && !storedKeys.has(key)) signatures.push({ name, signatureR2Key: key, signedAt: now, role: 'crew' });
  }
  if (form.supervisorSignatureKey && !storedKeys.has(form.supervisorSignatureKey)) {
    signatures.push({
      name: form.reviewedBy || 'Supervisor',
      signatureR2Key: form.supervisorSignatureKey,
      signedAt: now,
      role: 'supervisor',
    });
  }

  const body: Record<string, unknown> = {};
  const titleAfter = (form.title ?? '').trim();
  if (titleAfter !== (baseline.title ?? '').trim()) body.title = titleAfter || null;
  if (stepsChanged) body.jobSteps = stepsAfter;
  if (changedParts.includes('siteSpecificHazards')) body.siteSpecificHazards = hazardsAfter;
  if (changedParts.includes('emergencyProcedures')) body.emergencyProcedures = emergencyAfter;
  if (changedParts.includes('weatherConsiderations')) body.weatherConsiderations = weatherAfter;
  if (supervisorChanged) body.supervisorId = supervisorAfter;
  if (stableJson(signatures) !== stableJson(stored)) body.crewSignatures = signatures;

  return { body, stepsChanged, contentChanged, changedParts, supervisorChanged, cleared };
}

/** How many stored signatures the edit as it stands would clear — the warning's number. */
export function jsaSignaturesToClear(jsa: any, form: JsaEditFormState): number {
  return buildJsaUpdate(jsa, form).cleared;
}
